import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ClipboardList, Heart, FileText, ShieldCheck, ArrowRight, Table2, Baby, Stethoscope, Smartphone, Building2, Users, Link2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";

const RANCH_BG =
  "https://images.unsplash.com/photo-1701602029591-ed354736e28c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxob3JzZSUyMHJhbmNoJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc4MjgyMTgxOXww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: ClipboardList, title: "Daily Rounds", desc: "A dense, spreadsheet-style grid to log ovaries, fluid, edema, cervix & notes for every mare, every day." },
  { icon: Heart, title: "Teasing Chart", desc: "Track weekly teasing results across a Sun–Sat grid so you never miss a cycle." },
  { icon: FileText, title: "Breeding Cards", desc: "Generate print-ready per-mare Breeding Cards and bulk PDF exports for your vet in one click." },
  { icon: Baby, title: "Foal Registry", desc: "Foals are registered automatically when a mare's Foal Out Date is set — track sex, color, sire & dam in one place." },
  { icon: Stethoscope, title: "Foal Daily Rounds", desc: "Log newborn physical exams, umbilicus, medications & bandages with auto-scheduled next checks." },
  { icon: ShieldCheck, title: "Private & Secure", desc: "Every farm gets its own isolated workspace. Your mares, foals and records are yours alone." },
];

const SHOTS = [
  { src: "/shots/daily.png", title: "Daily Rounds + Foals", desc: "Log every mare's exam and add the day's foals to the roster." },
  { src: "/shots/history.png", title: "Mare History by season", desc: "Toggle between years to review every recorded exam for a mare." },
  { src: "/shots/foals.png", title: "Foal Registry", desc: "A running record of every foal born on the farm." },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/dashboard/daily", { replace: true });
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="flex items-center justify-between px-6 lg:px-12 py-5 max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <img src="/equimare-logo.png" alt="EquiMare Pro" className="h-9 w-9 rounded-xl object-cover" />
          <span className="font-extrabold text-xl tracking-tight">EquiMare Pro</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login">
            <Button variant="ghost" data-testid="landing-login-btn">Log in</Button>
          </Link>
          <Link to="/signup">
            <Button data-testid="landing-signup-btn">Start free trial</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center pt-10 pb-20">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-secondary text-secondary-foreground px-4 py-1.5 text-sm font-medium mb-6">
            <Table2 className="h-4 w-4" /> Built for breeding farms
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Track every mare&apos;s cycle. <span className="text-primary">Never miss a breeding window.</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-xl">
            EquiMare Pro turns your paper breeding spreadsheet into a fast, organized dashboard — daily rounds,
            teasing charts, auto-scheduled checks, vet-ready Breeding Cards, plus a full Foal Registry &amp; foal daily rounds
            once your babies hit the ground.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/signup">
              <Button size="lg" className="gap-2 text-base px-8" data-testid="hero-cta-btn">
                Start your 60-day free trial <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <span className="text-sm text-muted-foreground">No credit card required</span>
          </div>
        </div>
        <div className="relative">
          <div className="rounded-3xl overflow-hidden shadow-2xl border border-border aspect-[4/3]">
            <img src={RANCH_BG} alt="Ranch landscape" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-card border border-border rounded-2xl shadow-xl p-4 hidden sm:block">
            <p className="text-xs text-muted-foreground uppercase tracking-widest font-mono">Next check</p>
            <p className="text-2xl font-extrabold text-primary">Tomorrow</p>
            <p className="text-sm text-muted-foreground">Bella · 35mm F1</p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-secondary/40 border-y border-border py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-lg md:text-lg font-bold uppercase tracking-widest text-primary text-center mb-3 font-mono">
            Everything you tracked on paper
          </h2>
          <p className="text-center text-3xl sm:text-4xl font-extrabold tracking-tight mb-14">
            One dashboard for the whole season — from teasing to foaling
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="bg-card rounded-2xl border border-border p-6 hover:shadow-lg transition-shadow">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Screenshots */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <h2 className="text-lg font-bold uppercase tracking-widest text-primary text-center mb-3 font-mono">
          See it in action
        </h2>
        <p className="text-center text-3xl sm:text-4xl font-extrabold tracking-tight mb-14">
          Your whole barn, on one screen
        </p>
        <div className="grid md:grid-cols-3 gap-8">
          {SHOTS.map((s) => (
            <figure key={s.src} className="group" data-testid={`landing-shot-${s.title}`}>
              <div className="rounded-2xl overflow-hidden border border-border shadow-lg bg-card group-hover:shadow-2xl transition-shadow">
                <img src={s.src} alt={s.title} className="w-full h-auto object-cover object-top" loading="lazy" />
              </div>
              <figcaption className="mt-4">
                <p className="font-bold">{s.title}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* For Veterinarians */}
      <section className="bg-secondary/40 border-y border-border py-20" data-testid="landing-vet-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-4 py-1.5 text-sm font-semibold mb-6">
              <Stethoscope className="h-4 w-4" /> New · For Veterinarians
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
              One login for every client farm
            </h2>
            <p className="mt-5 text-lg text-muted-foreground max-w-xl">
              The Veterinarian plan lets you manage multiple farms and owners from a single account — each with its
              own private list of mares and foals. Switch from <em>The Barn</em> to <em>The Farm</em> in a tap, and email
              a breeding card straight to that farm.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                { icon: Building2, text: "Add unlimited client farms — each with its own owner & contact." },
                { icon: Users, text: "Every farm keeps a separate mare, teasing & foal list." },
                { icon: FileText, text: "\"Email to Farm\" sends the PDF breeding card to that farm's inbox." },
              ].map((row) => {
                const Icon = row.icon;
                return (
                  <li key={row.text} className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-foreground/90">{row.text}</span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-8">
              <Link to="/signup">
                <Button size="lg" className="gap-2 text-base px-8" data-testid="vet-cta-btn">
                  Start as a Veterinarian <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="bg-card border border-border rounded-3xl shadow-xl p-6">
            <p className="text-xs uppercase tracking-widest font-mono text-muted-foreground mb-4">Your client farms</p>
            <div className="space-y-3">
              {[
                { name: "The Barn", owner: "Jane Doe", mares: "Mares 1 · 2 · 3", active: true },
                { name: "The Farm", owner: "Sam Rivera", mares: "Mares 4 · 5 · 6", active: false },
              ].map((f) => (
                <div key={f.name} className={`flex items-center gap-3 rounded-2xl border p-4 ${f.active ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border"}`}>
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold leading-tight">{f.name}</p>
                    <p className="text-sm text-muted-foreground">{f.owner} · {f.mares}</p>
                  </div>
                  {f.active && (
                    <span className="text-xs font-bold uppercase tracking-wide text-primary bg-primary/10 rounded-full px-2.5 py-1">Active</span>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-start gap-3 mt-5 rounded-2xl border border-dashed border-border bg-secondary/50 p-4">
              <Link2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Coming soon:</strong> link to a farm&apos;s existing EquiMare account so
                vets and farm managers share the same live records.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile coming soon */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 flex flex-col md:flex-row items-center gap-8 md:gap-12">
          <div className="h-16 w-16 rounded-2xl bg-primary-foreground/10 flex items-center justify-center shrink-0">
            <Smartphone className="h-8 w-8" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/15 px-3 py-1 text-xs font-semibold uppercase tracking-widest mb-3">
              Coming soon
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">A mobile app for the barn aisle</h2>
            <p className="mt-3 text-primary-foreground/80 max-w-2xl">
              We&apos;re bringing EquiMare Pro to iOS and Android so you can run daily rounds, tap in teasing results and
              log foal checks right from your phone — even offline. The same records, synced to your farm&apos;s workspace.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Ready to run a smoother breeding season?</h2>
        <p className="mt-4 text-lg text-muted-foreground">Set up your farm&apos;s workspace in under a minute.</p>
        <Link to="/signup">
          <Button size="lg" className="mt-8 gap-2 text-base px-8" data-testid="cta-bottom-btn">
            Start free trial <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </section>

      <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
        <p>© {new Date().getFullYear()} EquiMare Pro · Equine mare management</p>
        <p className="mt-1">
          Questions?{" "}
          <a href="mailto:support@equimarepro.com" className="text-primary font-medium hover:underline" data-testid="footer-support-link">
            support@equimarepro.com
          </a>
        </p>
      </footer>
    </div>
  );
}
